import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, CaretDown, CaretRight, WarningOctagon, Hourglass, UserMinus, ArrowsLeftRight } from "@phosphor-icons/react";

const SEV_TONE = {
  Critical: "bg-rag-red/15 text-rag-red-fg border-rag-red/30",
  High: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  Medium: "bg-rag-amber/15 text-rag-amber-fg border-rag-amber/30",
  Low: "bg-sky-500/15 text-sky-500 border-sky-500/30",
};
const SEV = ["Critical", "High", "Medium", "Low"];

const fmtAge = (h) => {
  if (!h) return "—";
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

function SevChips({ by }) {
  const active = SEV.filter((s) => (by?.[s] || 0) > 0);
  if (active.length === 0) return <span className="text-xs text-muted-foreground">none</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {active.map((s) => (
        <span key={s} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${SEV_TONE[s]}`} data-testid={`sev-chip-${s}`}>
          {s[0]}·{by[s]}
        </span>
      ))}
    </div>
  );
}

function AnalystRow({ a, maxLoad, roster, onReassign, expanded, onToggle }) {
  const nav = useNavigate();
  const isUn = a.assignee_id === null;
  const pct = maxLoad ? Math.round((a.active / maxLoad) * 100) : 0;
  return (
    <div className="border border-border rounded-sm" data-testid={`load-analyst-${a.assignee_id || "unassigned"}`}>
      <button
        onClick={onToggle}
        data-testid={`load-toggle-${a.assignee_id || "unassigned"}`}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
      >
        {expanded ? <CaretDown size={14} className="shrink-0 text-muted-foreground" /> : <CaretRight size={14} className="shrink-0 text-muted-foreground" />}
        <div className="flex items-center gap-2 w-52 shrink-0">
          {isUn ? <UserMinus size={16} className="text-muted-foreground" /> : <div className="h-6 w-6 rounded-full bg-primary/15 text-primary grid place-items-center text-[10px] font-bold font-mono">{(a.assignee_name || "?").slice(0, 2).toUpperCase()}</div>}
          <span className={`text-sm truncate ${isUn ? "font-mono uppercase text-[11px] tracking-widest text-muted-foreground" : "font-medium"}`}>{a.assignee_name}</span>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[120px]">
          <div className="h-2 flex-1 max-w-[160px] bg-muted rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${isUn ? "bg-muted-foreground/40" : "bg-primary"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="font-display text-lg font-bold w-7 text-right" data-testid={`load-active-${a.assignee_id || "unassigned"}`}>{a.active}</span>
        </div>
        <div className="w-40 shrink-0 hidden md:block"><SevChips by={a.by_severity} /></div>
        <div className="w-24 shrink-0 hidden sm:flex items-center gap-3 justify-end">
          {a.breached > 0 && <span className="flex items-center gap-1 text-rag-red-fg text-xs font-mono" title="SLA breached"><WarningOctagon size={13} weight="fill" />{a.breached}</span>}
          {a.due_soon > 0 && <span className="flex items-center gap-1 text-rag-amber-fg text-xs font-mono" title="SLA due soon"><Hourglass size={13} />{a.due_soon}</span>}
        </div>
        <div className="w-16 shrink-0 text-right text-xs font-mono text-muted-foreground hidden lg:block" title="Oldest open case age">{fmtAge(a.oldest_age_hours)}</div>
      </button>

      {expanded && (
        <div className="border-t border-border divide-y divide-border/60" data-testid={`load-cases-${a.assignee_id || "unassigned"}`}>
          {a.cases.length === 0 && <div className="px-4 py-4 text-xs text-muted-foreground">No active cases.</div>}
          {a.cases.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/30 transition-colors">
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border shrink-0 ${SEV_TONE[c.severity]}`}>{c.severity}</span>
              <button onClick={() => nav(`/soc/cases/${c.id}`)} data-testid={`load-case-link-${c.id}`} className="font-mono text-xs text-primary hover:underline shrink-0">{c.number}</button>
              <span className="text-sm truncate flex-1">{c.title}</span>
              {c.resolution_breached && <span className="text-[10px] font-mono text-rag-red-fg shrink-0">BREACHED</span>}
              {!c.resolution_breached && c.sla_due_soon && <span className="text-[10px] font-mono text-rag-amber-fg shrink-0">DUE SOON</span>}
              <span className="text-[11px] font-mono text-muted-foreground w-12 text-right shrink-0">{fmtAge(c.age_hours)}</span>
              <Select value="" onValueChange={(v) => onReassign(c, v)}>
                <SelectTrigger data-testid={`reassign-trigger-${c.id}`} className="h-7 w-32 rounded-sm text-xs shrink-0">
                  <SelectValue placeholder="Reassign…" />
                </SelectTrigger>
                <SelectContent>
                  {roster.filter((r) => r.id !== a.assignee_id).map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`reassign-opt-${c.id}-${r.id}`}>{r.name}</SelectItem>
                  ))}
                  {a.assignee_id && <SelectItem value="__unassign__" data-testid={`reassign-opt-${c.id}-unassign`}>— Unassign —</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SocTeamLoad() {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/soc/metrics/caseload?client=${encodeURIComponent(client)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load caseload"))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => { api.get("/client-registry?module=soc").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const reassign = async (c, target) => {
    const assignee_id = target === "__unassign__" ? null : target;
    try {
      await api.post(`/soc/cases/${c.id}/assign`, { assignee_id });
      const name = assignee_id ? (data.roster.find((r) => r.id === assignee_id)?.name || "analyst") : "Unassigned";
      toast.success(`${c.number} → ${name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    }
  };

  const rows = data ? [data.unassigned, ...data.analysts] : [];
  const maxLoad = data ? Math.max(1, ...data.analysts.map((a) => a.active), data.unassigned.active) : 1;
  const totalActive = data ? data.analysts.reduce((s, a) => s + a.active, 0) + data.unassigned.active : 0;
  const activeAnalysts = data ? data.analysts.filter((a) => a.active > 0).length : 0;
  const avgLoad = activeAnalysts ? Math.round((totalActive - (data?.unassigned.active || 0)) / activeAnalysts) : 0;

  return (
    <div className="p-6 space-y-6" data-testid="soc-team-load-view">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1 flex items-center gap-2"><ArrowsLeftRight size={13} /> Workload Balancing</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Team Load</h1>
          <p className="text-sm text-muted-foreground mt-1">Live active caseload per analyst. Reassign cases to balance the queue.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Client</span>
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger data-testid="load-client-selector" className="w-52 rounded-sm h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border-border rounded-sm" data-testid="load-kpi-active">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Active Cases</div>
            <div className="font-display text-2xl font-bold">{totalActive}</div>
          </Card>
          <Card className="p-4 border-border rounded-sm" data-testid="load-kpi-unassigned">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Unassigned</div>
            <div className={`font-display text-2xl font-bold ${data.unassigned.active > 0 ? "text-rag-amber-fg" : ""}`}>{data.unassigned.active}</div>
          </Card>
          <Card className="p-4 border-border rounded-sm" data-testid="load-kpi-analysts">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Analysts w/ Load</div>
            <div className="font-display text-2xl font-bold">{activeAnalysts}</div>
          </Card>
          <Card className="p-4 border-border rounded-sm" data-testid="load-kpi-avg">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Avg / Analyst</div>
            <div className="font-display text-2xl font-bold">{avgLoad}</div>
          </Card>
        </div>
      )}

      <Card className="p-0 border-border rounded-sm overflow-hidden">
        <div className="hidden md:flex items-center gap-4 px-4 py-2 border-b border-border text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/30">
          <span className="w-3.5" />
          <span className="w-52 shrink-0 flex items-center gap-1"><Users size={12} /> Analyst</span>
          <span className="flex-1">Load</span>
          <span className="w-40 shrink-0">Severity</span>
          <span className="w-24 shrink-0 text-right">SLA</span>
          <span className="w-16 shrink-0 text-right hidden lg:block">Oldest</span>
        </div>
        <div className="p-3 space-y-2">
          {loading && !data && <div className="py-8 text-center text-muted-foreground text-sm">Loading caseload…</div>}
          {rows.map((a) => (
            <AnalystRow
              key={a.assignee_id || "unassigned"}
              a={a}
              maxLoad={maxLoad}
              roster={data.roster}
              onReassign={reassign}
              expanded={!!expanded[a.assignee_id || "unassigned"]}
              onToggle={() => setExpanded((e) => ({ ...e, [a.assignee_id || "unassigned"]: !e[a.assignee_id || "unassigned"] }))}
            />
          ))}
          {data && rows.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">No active cases.</div>}
        </div>
      </Card>
    </div>
  );
}
